import { useMutation, useQuery, useQueryClient } from 'react-query';
import { bffInstance } from '../../api/axiosInstance';

type ReqBody = {
    name: string;
    description: string;
    url: string;
    target: {
        department: string[] | null;
    };
    startDate: any;
    endDate: any;
    archivalDate?: any;
    lastUpdatedBy?: string;
    lastUpdatedOn?: any;
    createdBy?: string;
    createdOn?: any;
    enterpriseId: string;
    loginId: string;
}

export interface IDepartment {
    id: string;
    label: string;
    value: string;
    codes?: string[];
}

const fetchListOfLinkNotification = async (enterpriseId: string) => {
    const res = await bffInstance.get<Record<any, any>[]>(`/getCustomApps/${enterpriseId}`);
    return res.data;
};

const fetchListOfArchiveLinkNotification = async (enterpriseId: string) => {
    const res = await bffInstance.get<Record<any, any>[]>(`/history/getCustomApps/${enterpriseId}`);
    return res.data;
};

const fetchDepartments = async () => {
    const res = await bffInstance.get<any[]>('/getDepartments');
    // Transform API response to match our format
    const departmentList = res.data.map((item: any) => ({
        id: item.departmentId,
        label: item.departmentLabel,
        value: item.departmentValue,
        codes: item.departmentCodes || []
    }));
    return departmentList;
};

const createCustomCard = async (requestBody: Partial<ReqBody>) => {
    const res = await bffInstance.post('/createCustomApp', requestBody);
    return res.data;
};

const deleteCustomCard = async (requestBody: { appId: string; enterpriseId: string }) => {
    const res = await bffInstance.patch('/deleteCustomApp', requestBody);
    return res.data;
};

const updateCustomCard = async (requestBody: Partial<ReqBody> & { appId: string }) => {
    const res = await bffInstance.patch('/updateCustomApp', requestBody);
    return res.data;
};

export function useGetAllNotifications<T>(isArchive: boolean, enterpriseId?: string) {
    return useQuery<T>(
        !isArchive 
            ? ['getAllLinkNotifications', enterpriseId] 
            : ['getAllArchiveLinkNotifications', enterpriseId],
        () => (!isArchive 
            ? fetchListOfLinkNotification(enterpriseId!)
            : fetchListOfArchiveLinkNotification(enterpriseId!)) as Promise<T>,
        {
            enabled: !!enterpriseId,
        }
    );
}

export function useGetDepartments() {
    return useQuery(
        'departmentsList',
        () => fetchDepartments()
    );
}

export const useCreateLinkNotification = ({ onSuccess, onError }: any) => {
    const queryClient = useQueryClient();

    const mutation = useMutation(
        (model: Partial<ReqBody>) => createCustomCard(model),
        {
            onError: (error: any) => {
                onError(error);
                console.error('Error creating link:', error);
            },
            onSuccess: (data: any) => {
                onSuccess(data);
                queryClient.invalidateQueries(['getAllLinkNotifications']);
            },
        }
    );

    return { mutation };
};

export const useUpdateLinkNotification = ({ onSuccess, onError }: any) => {
    const queryClient = useQueryClient();

    const mutation = useMutation(
        (model: Partial<ReqBody> & { appId: string }) => updateCustomCard(model),
        {
            onError: (error: any) => {
                onError();
                console.error('Error updating link:', error);
            },
            onSuccess: (data: any) => {
                onSuccess(data);
                queryClient.invalidateQueries(['getAllLinkNotifications']);
            },
        }
    );

    return { mutation };
};

export const useRemoveLinkNotification = ({ onSuccess, onError }: any) => {
    const queryClient = useQueryClient();

    const mutation = useMutation(
        (model: { appId: string; enterpriseId: string }) => deleteCustomCard(model),
        {
            onError: (error: any) => {
                onError();
                console.error('Error deleting link:', error);
            },
            onSuccess: (_data, model) => {
                onSuccess(model.appId);
                queryClient.invalidateQueries(['getAllLinkNotifications']);
            },
        }
    );

    return { mutation };
};
